// /admin/roles/new and /admin/roles/:roleId/edit — v6 mockup
// data-view="role-create".
//
// Three sections in a card frame: Identity, Permissions, Preview. Footer
// shows "<n> permissions selected" + Cancel/Save. tenant_admin redirects
// to detail (uneditable). Edit mode locks role_id and pulls version for
// optimistic-lock writes.

import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { useToast } from "@/components";
import { roleClient } from "@/data/clients/roleClient";
import { permissionClient } from "@/data/clients/permissionClient";
import {
  permissionDisplayName,
  type PermissionCatalog,
  type PermissionEntry,
  type PermissionGroup,
  type Role,
} from "@/types/role";
import "../AdminShared.css";

const RESERVED_ROLE_ID = "tenant_admin";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Plain-English summary of the selected permissions, grouped by resource.
 *  Used in the "Preview" section. */
function describePermissions(
  catalog: PermissionCatalog | null,
  selected: Set<string>,
): { can: string[]; cant: string[] } {
  const can: string[] = [];
  const cant: string[] = [];
  if (!catalog) return { can, cant };
  for (const group of catalog.groups) {
    for (const perm of group.permissions) {
      const label = permissionDisplayName(perm).toLowerCase();
      if (selected.has(perm.key)) can.push(label);
      else if (!perm.is_reserved) cant.push(label);
    }
  }
  return { can, cant };
}

export function RoleFormPage(): JSX.Element {
  const { roleId: editRoleId } = useParams<{ roleId: string }>();
  const isEdit = Boolean(editRoleId);
  const [searchParams] = useSearchParams();
  const duplicateOf = searchParams.get("duplicate") ?? "";
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();

  const [displayName, setDisplayName] = useState("");
  const [formRoleId, setFormRoleId] = useState("");
  const [description, setDescription] = useState("");
  const [permissionSet, setPermissionSet] = useState<Set<string>>(new Set());
  const [version, setVersion] = useState(0);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Auto-slug as long as the user hasn't typed an explicit role_id.
  const [roleIdTouched, setRoleIdTouched] = useState(false);

  // Bootstrap: catalog + (if editing) the role we're editing, or (if
  // duplicating) the role we're duplicating from.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cat = await permissionClient.getCatalog();
        if (cancelled) return;
        setCatalog(cat);

        const sourceRoleId = editRoleId ?? duplicateOf;
        if (sourceRoleId) {
          const role: Role = await roleClient.get(sourceRoleId);
          if (cancelled) return;
          if (isEdit && role.role_id === RESERVED_ROLE_ID) {
            toast.warning({
              title: "tenant_admin can't be edited",
              description: "Returning to the role detail.",
            });
            navigate(`/admin/roles/${encodeURIComponent(role.role_id)}`, {
              replace: true,
            });
            return;
          }
          setDisplayName(
            isEdit ? role.display_name : `${role.display_name} (copy)`,
          );
          setFormRoleId(isEdit ? role.role_id : slugify(`${role.display_name}-copy`));
          setRoleIdTouched(true); // don't re-slug after this point
          setDescription(role.description);
          setPermissionSet(new Set(role.permission_set));
          setVersion(role.version);
        }
      } catch (err) {
        if (err instanceof ApiError) {
          handleApiError(err, { actionLabel: "Load role" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // We deliberately exclude toast/navigate/handleApiError — stable for
    // the lifetime of this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRoleId, duplicateOf, isEdit]);

  // Auto-slug role_id from display_name in create mode until the user
  // touches the role_id field.
  useEffect(() => {
    if (isEdit) return;
    if (roleIdTouched) return;
    setFormRoleId(slugify(displayName));
  }, [displayName, isEdit, roleIdTouched]);

  // -------------------------------------------------------------------------
  // Permission toggling
  // -------------------------------------------------------------------------
  const togglePerm = useCallback((key: string) => {
    setPermissionSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleGroup = useCallback(
    (group: PermissionGroup, on: boolean) => {
      setPermissionSet((prev) => {
        const next = new Set(prev);
        for (const p of group.permissions) {
          if (p.is_reserved) continue;
          if (on) next.add(p.key);
          else next.delete(p.key);
        }
        return next;
      });
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const canSave =
    displayName.trim().length > 0 &&
    formRoleId.trim().length > 0 &&
    permissionSet.size > 0 &&
    !submitting;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      if (isEdit && editRoleId) {
        const updated = await roleClient.update(
          editRoleId,
          {
            display_name: displayName.trim(),
            description: description.trim(),
            permission_set: Array.from(permissionSet),
          },
          version,
        );
        toast.success({
          title: `Role '${displayName.trim()}' saved`,
          description: `${updated.summary.grants_added} grants added · ${updated.summary.grants_removed} removed`,
        });
        navigate(`/admin/roles/${encodeURIComponent(editRoleId)}`);
      } else {
        const created = await roleClient.create({
          role_id: formRoleId.trim(),
          display_name: displayName.trim(),
          description: description.trim(),
          permission_set: Array.from(permissionSet),
        });
        toast.success({ title: `Role '${created.display_name}' created` });
        navigate(`/admin/roles/${encodeURIComponent(created.role_id)}`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Save role" });
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    canSave,
    isEdit,
    editRoleId,
    formRoleId,
    displayName,
    description,
    permissionSet,
    version,
    toast,
    navigate,
    handleApiError,
  ]);

  // -------------------------------------------------------------------------
  // Preview text
  // -------------------------------------------------------------------------
  const preview = useMemo(
    () => describePermissions(catalog, permissionSet),
    [catalog, permissionSet],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <section className="admin-page">
        <div className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/roles">← Roles</Link>
          </div>
          <div className="row">
            <h1>Loading…</h1>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <div className="v6-page-header">
        <div className="breadcrumbs">
          <Link to="/admin/roles">← Roles</Link>
        </div>
        <div className="row">
          <h1>{isEdit ? "Edit role" : "New custom role"}</h1>
        </div>
        <div className="caption">
          Build a role only when none of the system templates fit. A clear
          name and description make it easier to audit later — and to
          retire when it&rsquo;s no longer needed.
        </div>
      </div>

      <div className="v6-role-create-frame">
        {/* Identity */}
        <div className="v6-role-create-section">
          <h3 className="v6-rcs-title">Identity</h3>

          <label className="v6-rcs-label">
            Display name{" "}
            <span className="v6-rcs-hint">
              — what people see in role chips and the wizard
            </span>
          </label>
          <input
            className="v6-rcs-input"
            type="text"
            value={displayName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDisplayName(e.target.value)
            }
            placeholder="e.g. Floor Manager"
          />

          <label className="v6-rcs-label">
            Role ID{" "}
            <span className="v6-rcs-hint">
              — stable identifier for audit logs and APIs (lowercase, hyphens)
            </span>
          </label>
          <input
            className="v6-rcs-input mono"
            type="text"
            value={formRoleId}
            disabled={isEdit}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setRoleIdTouched(true);
              setFormRoleId(e.target.value);
            }}
            placeholder="floor-manager"
          />

          <label className="v6-rcs-label">
            Description <span className="v6-rcs-hint">— optional</span>
          </label>
          <textarea
            className="v6-rcs-input"
            rows={2}
            value={description}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setDescription(e.target.value)
            }
            placeholder="What does someone with this role typically do?"
          />
        </div>

        {/* Permissions */}
        <div className="v6-role-create-section">
          <h3 className="v6-rcs-title">Permissions</h3>
          <p className="v6-rcs-helper">
            Pick what this role can do. Reserved permissions are admin-only
            and limited to system roles.
          </p>

          <div className="v6-perm-grid">
            {catalog?.groups.map((group) =>
              renderGroup(group, permissionSet, togglePerm, toggleGroup),
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="v6-role-create-section preview">
          <h3 className="v6-rcs-title">Preview</h3>
          <div className="v6-english-panel" style={{ marginTop: 0 }}>
            {preview.can.length === 0 ? (
              <>Pick at least one permission to see what this role will allow.</>
            ) : (
              <>
                Someone with the <b>{displayName.trim() || "this"}</b> role
                will be able to{" "}
                {preview.can.slice(0, 4).map((p, i) => (
                  <span key={p}>
                    {i > 0 && i === preview.can.length - 1 && preview.can.length <= 4
                      ? ", and "
                      : i > 0
                        ? ", "
                        : ""}
                    <b>{p}</b>
                  </span>
                ))}
                {preview.can.length > 4 ? `, and ${preview.can.length - 4} more` : ""}
                .
                {preview.cant.length > 0 ? (
                  <>
                    {" "}
                    They <b>won&rsquo;t</b> be able to{" "}
                    {preview.cant.slice(0, 3).map((p, i) => (
                      <span key={p}>
                        {i > 0 && i === Math.min(preview.cant.length - 1, 2)
                          ? ", or "
                          : i > 0
                            ? ", "
                            : ""}
                        <b>{p}</b>
                      </span>
                    ))}
                    .
                  </>
                ) : null}
              </>
            )}
          </div>
          <p className="v6-rcs-helper" style={{ marginTop: 10 }}>
            Once saved, you can grant this role to a group from{" "}
            <Link
              to="/admin/people"
              style={{
                color: "var(--ring-primary-500)",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Groups &amp; users
            </Link>{" "}
            or directly when adding a rule.
          </p>
        </div>

        {/* Footer */}
        <div className="v6-wizard-footer">
          <div className="footer-left">
            {permissionSet.size} permission{permissionSet.size === 1 ? "" : "s"}{" "}
            selected
          </div>
          <div className="footer-right">
            <button
              type="button"
              className="v6-btn ghost"
              onClick={() => navigate("/admin/roles")}
            >
              Cancel
            </button>
            <button
              type="button"
              className="v6-btn primary"
              disabled={!canSave}
              onClick={() => void handleSave()}
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              {isEdit ? "Save changes" : "Save role"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helper: render a permission group block
// ---------------------------------------------------------------------------
function renderGroup(
  group: PermissionGroup,
  selected: Set<string>,
  togglePerm: (key: string) => void,
  toggleGroup: (group: PermissionGroup, on: boolean) => void,
): JSX.Element {
  const togglable = group.permissions.filter((p) => !p.is_reserved);
  const allReserved = togglable.length === 0;
  const heldInGroup = group.permissions.filter((p) => selected.has(p.key)).length;
  const allOn = togglable.length > 0 && togglable.every((p) => selected.has(p.key));

  return (
    <div
      className={allReserved ? "v6-perm-group reserved" : "v6-perm-group"}
      key={group.key}
    >
      <div className="v6-perm-group-head">
        {allReserved ? (
          <span className="v6-perm-group-title">{group.label}</span>
        ) : (
          <label className="v6-perm-group-toggle">
            <input
              type="checkbox"
              checked={allOn}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                toggleGroup(group, e.target.checked)
              }
            />
            <span className="v6-perm-group-title">{group.label}</span>
          </label>
        )}
        <span
          className={
            allReserved
              ? "v6-perm-group-count reserved"
              : "v6-perm-group-count"
          }
        >
          {allReserved
            ? "reserved · system roles only"
            : `${heldInGroup} of ${group.permissions.length}`}
        </span>
      </div>
      {group.permissions.map((perm) => renderPerm(perm, selected, togglePerm))}
    </div>
  );
}

function renderPerm(
  perm: PermissionEntry,
  selected: Set<string>,
  togglePerm: (key: string) => void,
): JSX.Element {
  if (perm.is_reserved) {
    return (
      <div className="v6-perm-row reserved" key={perm.key}>
        <span className="v6-perm-checkbox disabled" aria-hidden="true" />
        <div>
          <div className="perm-name">
            {permissionDisplayName(perm)}
            <span className="perm-tag">reserved</span>
          </div>
          <div className="perm-key">{perm.key}</div>
        </div>
      </div>
    );
  }
  const isSelected = selected.has(perm.key);
  return (
    <label
      className={
        isSelected
          ? "v6-perm-row pickable selected"
          : "v6-perm-row pickable"
      }
      key={perm.key}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => togglePerm(perm.key)}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
      />
      <span className="v6-perm-checkbox" aria-hidden="true" />
      <div>
        <div className="perm-name">{permissionDisplayName(perm)}</div>
        <div className="perm-key">{perm.key}</div>
      </div>
    </label>
  );
}
